import { useId, useRef, useState, type ChangeEvent, type ClipboardEvent, type KeyboardEvent, type ReactNode } from 'react';
import { AlertCircle, Check, FileText, Globe, Loader2, Upload, X } from 'lucide-react';
import {
  BRAND_IMPORT_ERROR_TEXT,
  BRAND_LIMITS,
  normalizeWebsiteUrl,
  WEBSITE_URL_ERROR_TEXT,
  type BrandImportJobView,
  type BrandImportPageView,
} from '@mg/shared';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Field';
import { cn } from '../../../lib/cn';
import { URL_SCAN_ENABLED } from '../../brand/flags/urlScan';
import { DOCUMENT_BOX_COPY, REVISIT_SOURCES_COPY as COPY } from '../wizardCopy';
import { DOCUMENT_ACCEPT, formatFileSize } from './fileCheck';
import { insertRows, pastedLines, pageStatusText, URL_BOX_COPY } from './UrlListBox';

/** The id of the "Add URL" address box: Next puts the focus there when it refuses what is typed. */
export const REVISIT_PAGE_INPUT_ID = 'brand-setup-revisit-page';

// ── Shared pieces ───────────────────────────────────────────────────────────────

/** A source box with a bordered header (icon and title), then its body. */
function SourceSection({ icon, heading, help, children }: { icon: ReactNode; heading: string; help: string; children: ReactNode }) {
  const headingId = useId();
  return (
    <section aria-labelledby={headingId} className="overflow-hidden rounded-xl border border-line bg-card">
      <div className="flex items-center gap-2 border-b border-line bg-page px-4 py-3">
        {icon}
        <h3 id={headingId} className="text-sm font-semibold text-ink-strong">
          {heading}
        </h3>
      </div>
      <div className="space-y-3 p-4">
        <p className="text-sm text-ink-muted">{help}</p>
        {children}
      </div>
    </section>
  );
}

type RowTone = 'read' | 'failed' | 'muted';

/** How a source row says it went: a check for Read, an alert for a failure, a spinner while reading. */
function RowStatus({ tone, spinning = false, children }: { tone: RowTone; spinning?: boolean; children: ReactNode }) {
  return (
    <span
      className={cn(
        'flex items-center gap-1 text-xs',
        tone === 'read' && 'text-success dark:text-success-dark',
        tone === 'failed' && 'text-red-600 dark:text-error-dark',
        tone === 'muted' && 'text-ink-muted',
      )}
    >
      {tone === 'read' && <Check className="h-3.5 w-3.5 shrink-0" aria-hidden />}
      {tone === 'failed' && <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden />}
      {spinning && <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin motion-reduce:animate-none" aria-hidden />}
      {children}
    </span>
  );
}

/** One source as a row: its name and status on the left, the remove button on the right. */
function SourceRow({
  icon,
  title,
  status,
  action,
  removeLabel,
  onRemove,
  disabled,
}: {
  icon?: ReactNode;
  title: string;
  status: ReactNode;
  action?: ReactNode;
  removeLabel?: string;
  onRemove?: () => void;
  disabled: boolean;
}) {
  return (
    <li className="flex items-start gap-3 rounded-lg border border-line bg-page px-3 py-2">
      {icon}
      <div className="min-w-0 flex-1">
        <p className="break-all text-sm text-ink">{title}</p>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1">
          {status}
          {action}
        </div>
      </div>
      {onRemove && removeLabel && (
        <Button
          type="button"
          variant="plain"
          size="sm"
          className="w-8 shrink-0 px-0"
          disabled={disabled}
          aria-label={removeLabel}
          onClick={onRemove}
        >
          <X className="h-4 w-4" aria-hidden />
        </Button>
      )}
    </li>
  );
}

// ── The document ────────────────────────────────────────────────────────────────

/**
 * Revisit's document box: the current document as a row with how it was read and a remove button, or the
 * file chosen to replace it, then Replace document (Upload document when there is none). Nothing is sent
 * until Next re-reads the sources.
 */
export function RevisitDocumentBox({
  current,
  removed,
  pendingFile,
  error,
  uploading,
  uploadProgress,
  disabled,
  onChoose,
  onRemovePending,
  onRemoveCurrent,
  onUndoRemove,
}: {
  current: BrandImportJobView['document'];
  removed: boolean;
  pendingFile: File | null;
  error: string | null;
  uploading: boolean;
  uploadProgress: number | null;
  disabled: boolean;
  onChoose: (file: File) => void;
  onRemovePending: () => void;
  onRemoveCurrent: () => void;
  onUndoRemove: () => void;
}) {
  const inputId = useId();
  const input = useRef<HTMLInputElement>(null);
  const onPick = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Cleared, so choosing the same file again (after removing it) is still a change.
    event.target.value = '';
    if (file) onChoose(file);
  };
  const percent = uploadProgress === null ? null : Math.round(uploadProgress * 100);
  const fileIcon = <FileText className="mt-0.5 h-4 w-4 shrink-0 text-ink-muted" aria-hidden />;
  const shown = pendingFile !== null || (current !== null && !removed);

  let row: ReactNode = null;
  if (pendingFile) {
    row = (
      <SourceRow
        icon={fileIcon}
        title={`${pendingFile.name} · ${formatFileSize(pendingFile.size)}`}
        status={<RowStatus tone="muted">{COPY.fresh}</RowStatus>}
        removeLabel={COPY.removeDocument(pendingFile.name)}
        onRemove={onRemovePending}
        disabled={disabled}
      />
    );
  } else if (current && removed) {
    row = (
      <SourceRow
        icon={fileIcon}
        title={current.fileName}
        status={<RowStatus tone="muted">{COPY.documentRemoved}</RowStatus>}
        action={
          <Button type="button" variant="plain" size="sm" disabled={disabled} onClick={onUndoRemove}>
            {COPY.undoRemove}
          </Button>
        }
        disabled={disabled}
      />
    );
  } else if (current) {
    const status =
      current.status === 'read' ? (
        <RowStatus tone="read">{URL_BOX_COPY.read}</RowStatus>
      ) : current.status === 'failed' ? (
        <RowStatus tone="failed">{BRAND_IMPORT_ERROR_TEXT[current.error ?? 'ai_failed']}</RowStatus>
      ) : (
        <RowStatus tone="muted" spinning>
          {URL_BOX_COPY.reading}
        </RowStatus>
      );
    row = (
      <SourceRow
        icon={fileIcon}
        title={current.fileName}
        status={status}
        removeLabel={COPY.removeDocument(current.fileName)}
        onRemove={onRemoveCurrent}
        disabled={disabled}
      />
    );
  }

  return (
    <SourceSection
      icon={<FileText className="h-4 w-4 shrink-0 text-ink-muted" aria-hidden />}
      heading={COPY.documentHeading}
      help={COPY.documentHelp}
    >
      {row && <ul>{row}</ul>}
      <label htmlFor={inputId} className="sr-only">
        {DOCUMENT_BOX_COPY.inputLabel}
      </label>
      <input
        ref={input}
        id={inputId}
        type="file"
        accept={DOCUMENT_ACCEPT}
        className="sr-only"
        // The button below is the control; the input stays out of the Tab order.
        tabIndex={-1}
        disabled={disabled}
        onChange={onPick}
      />
      <div className="flex flex-wrap items-center gap-3">
        <Button type="button" variant="secondary" size="sm" disabled={disabled} onClick={() => input.current?.click()}>
          <Upload className="h-4 w-4" aria-hidden />
          {shown ? COPY.replaceDocument : COPY.uploadDocument}
        </Button>
        {/* Always in the page, so the progress is read out as it changes. */}
        <p role="status" className="text-sm text-ink-muted">
          {uploading ? DOCUMENT_BOX_COPY.uploading(percent) : null}
        </p>
      </div>
      {error && (
        <p role="alert" className="text-sm text-red-600 dark:text-error-dark">
          {error}
        </p>
      )}
    </SourceSection>
  );
}

// ── The web pages ───────────────────────────────────────────────────────────────

/** A page row's status: how its last read went, in setup's words (pageStatusText), or that Next will read it. */
function pageStatus(page: BrandImportPageView | undefined, retrying: boolean): { tone: RowTone; text: string; spinning?: boolean } {
  if (!page) return { tone: 'muted', text: COPY.fresh };
  if (page.status === 'failed' && retrying) return { tone: 'muted', text: COPY.retrying };
  const tone: RowTone = page.status === 'read' ? 'read' : page.status === 'failed' ? 'failed' : 'muted';
  return { tone, text: pageStatusText(page), spinning: page.status === 'pending' };
}

/**
 * Revisit's web-page box: each page as a row with how its last read went (the spec's words for one that
 * failed, with Try again) and a remove button, then one address box with Add URL. Enter adds too; a paste
 * of several lines adds each, without a page already listed and without passing BRAND_LIMITS.urls. An
 * address is checked as it is added, so the list holds public web addresses only, normalized. What is
 * still typed in the box when Next is clicked is read as one more page (useWizardController).
 * Renders nothing while features/brand/flags/urlScan.ts is off.
 */
export function RevisitPageList(props: {
  urls: string[];
  onChange: (urls: string[]) => void;
  /** The address typed in the box, not added yet. */
  draft: string;
  onDraftChange: (text: string) => void;
  /** The pages of the latest import, with how each went. */
  stored: readonly BrandImportPageView[];
  retrying: readonly string[];
  onRetry: (url: string) => void;
  disabled: boolean;
}) {
  if (!URL_SCAN_ENABLED) return null;
  return <PageList {...props} />;
}

function PageList({
  urls,
  onChange,
  draft,
  onDraftChange,
  stored,
  retrying,
  onRetry,
  disabled,
}: Parameters<typeof RevisitPageList>[0]) {
  const noteId = useId();
  const [note, setNote] = useState<{ text: string; error: boolean } | null>(null);
  const [said, setSaid] = useState('');
  const full = urls.length >= BRAND_LIMITS.urls;
  const byUrl = new Map(stored.map((page) => [page.url, page]));

  const add = () => {
    const text = draft.trim();
    if (text === '') return;
    if (full) {
      setNote({ text: URL_BOX_COPY.full, error: false });
      return;
    }
    const result = normalizeWebsiteUrl(text);
    if (!result.ok) {
      setNote({ text: WEBSITE_URL_ERROR_TEXT[result.error], error: true });
      return;
    }
    if (urls.includes(result.url)) {
      setNote({ text: URL_BOX_COPY.duplicate, error: false });
      return;
    }
    onChange([...urls, result.url]);
    onDraftChange('');
    setNote(null);
    setSaid(COPY.added(result.url));
  };

  const remove = (url: string) => {
    onChange(urls.filter((u) => u !== url));
    setSaid(URL_BOX_COPY.removed(url));
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter' || event.nativeEvent.isComposing) return;
    event.preventDefault();
    add();
  };

  const onPaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const lines = pastedLines(event.clipboardData.getData('text/plain'));
    // One address is pasted into the box as usual.
    if (lines.length < 2) return;
    event.preventDefault();
    const valid = lines.filter((line) => normalizeWebsiteUrl(line).ok);
    const invalid = lines.filter((line) => !normalizeWebsiteUrl(line).ok);
    const outcome = insertRows(urls, urls.length, valid);
    if (outcome.added > 0) onChange(outcome.rows);
    // What is not a web address stays in the box, to fix and add, rather than being dropped.
    onDraftChange(invalid.join(' '));
    const parts = [
      outcome.added > 0 ? URL_BOX_COPY.added(outcome.added) : null,
      outcome.duplicates > 0 ? URL_BOX_COPY.alreadyListed(outcome.duplicates) : null,
      outcome.overflow > 0 ? URL_BOX_COPY.overflow(outcome.overflow) : null,
      invalid.length > 0 ? COPY.notAddresses(invalid.length) : null,
    ].filter((part): part is string => part !== null);
    setNote(parts.length > 0 ? { text: parts.join(' '), error: invalid.length > 0 } : null);
  };

  return (
    <SourceSection
      icon={<Globe className="h-4 w-4 shrink-0 text-ink-muted" aria-hidden />}
      heading={COPY.pagesHeading}
      help={COPY.pagesHelp}
    >
      {urls.length > 0 && (
        <ul className="space-y-2">
          {urls.map((url) => {
            const page = byUrl.get(url);
            const isRetrying = retrying.includes(url);
            const status = pageStatus(page, isRetrying);
            const canRetry = page?.status === 'failed' && !isRetrying;
            return (
              <SourceRow
                key={url}
                title={url}
                status={
                  <RowStatus tone={status.tone} spinning={status.spinning}>
                    {status.text}
                  </RowStatus>
                }
                action={
                  canRetry ? (
                    <Button type="button" variant="plain" size="sm" disabled={disabled} onClick={() => onRetry(url)}>
                      {COPY.tryAgain}
                    </Button>
                  ) : undefined
                }
                removeLabel={COPY.removePage(url)}
                onRemove={() => remove(url)}
                disabled={disabled}
              />
            );
          })}
        </ul>
      )}

      <div className="flex items-center gap-2">
        <label htmlFor={REVISIT_PAGE_INPUT_ID} className="sr-only">
          {COPY.pageInputLabel}
        </label>
        <Input
          id={REVISIT_PAGE_INPUT_ID}
          type="text"
          inputMode="url"
          autoComplete="url"
          autoCapitalize="none"
          spellCheck={false}
          value={draft}
          disabled={disabled || full}
          placeholder={COPY.pagePlaceholder}
          aria-describedby={note ? noteId : undefined}
          aria-invalid={note?.error ? true : undefined}
          onChange={(e) => {
            setNote(null);
            onDraftChange(e.target.value);
          }}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          className="min-w-0 flex-1"
        />
        <Button type="button" variant="secondary" size="sm" disabled={disabled || full || draft.trim() === ''} onClick={add}>
          {COPY.addPage}
        </Button>
      </div>
      {full && !note && <p className="text-sm text-ink-muted">{URL_BOX_COPY.full}</p>}
      {note && (
        <p id={noteId} className={cn('text-xs', note.error ? 'text-red-600 dark:text-error-dark' : 'text-ink-muted')}>
          {note.text}
        </p>
      )}
      {/* Always in the page, so an addition or a removal is read out. */}
      <p role="status" className="sr-only">
        {said}
      </p>
    </SourceSection>
  );
}
